import { LEGAL_VERSION } from '@lista/shared-types';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { LanguageSwitch } from '../../shared/layout/LanguageSwitch';
import { ThemeToggle } from '../../shared/layout/ThemeToggle';
import { LegalLinks } from './LegalLinks';
import styles from './legal.module.css';

interface Section {
  title: string;
  body: string[];
}

/** Public, unauthenticated pages: /privacy and /terms (texts in the i18n catalogs, `legal.*`). */
export function LegalPage({ doc }: { doc: 'privacy' | 'terms' }) {
  const { t, i18n } = useTranslation();
  const title = t(`legal.${doc}.title`);
  const sections = t(`legal.${doc}.sections`, { returnObjects: true }) as Section[];
  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(
    new Date(`${LEGAL_VERSION}T00:00:00`),
  );

  useEffect(() => {
    document.title = `${title} · Lista`;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/" className={styles.logo} aria-label={t('legal.nav.back')}>
          Lista
        </Link>
        <div className={styles.tools}>
          <ThemeToggle className={styles.tool} />
          <LanguageSwitch className={styles.tool} />
        </div>
      </header>
      <main className={styles.main}>
        <article className={styles.article} aria-labelledby="legal-title">
          <h1 id="legal-title">{title}</h1>
          <p className={styles.updated}>{t('legal.updated', { date })}</p>
          <p className={styles.intro}>{t(`legal.${doc}.intro`)}</p>
          {sections.map((s) => (
            <section key={s.title} aria-label={s.title}>
              <h2>{s.title}</h2>
              <Body lines={s.body} />
            </section>
          ))}
        </article>
      </main>
      <footer className={styles.footer}>
        <LegalLinks />
      </footer>
    </div>
  );
}

/** Paragraphs, with consecutive "• " lines grouped into one bullet list. */
function Body({ lines }: { lines: string[] }) {
  const blocks: Array<string | string[]> = [];
  for (const line of lines) {
    if (line.startsWith('• ')) {
      const last = blocks.at(-1);
      if (Array.isArray(last)) last.push(line.slice(2));
      else blocks.push([line.slice(2)]);
    } else blocks.push(line);
  }
  return (
    <>
      {blocks.map((b, i) =>
        Array.isArray(b) ? (
          <ul key={i}>
            {b.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : (
          <p key={i}>{b}</p>
        ),
      )}
    </>
  );
}
